import { useAuth0 } from "@auth0/auth0-react";
import { InfoRounded } from "@mui/icons-material";
import { Box, Button, Divider, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import useSmartAndRegularAssets from "../../../../../api/hooks/useSmartAndRegularAssets";
import { stepKeys } from "../../../../../api/queryKeys";
import { default as CustomToolTip } from "../../../../../components/CustomTooltip";
import LabelComponentProp from "../../../../../components/LabelComponentProp";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { CustomerFeatureFlagsContext } from "../../../../../contexts/CustomerFeatureFlagsContext";
import { EnvFeatureFlagContext } from "../../../../../contexts/EnvFeaturesContext";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { formatNumberCompact } from "../../../../../utils/formatters";
import {
  detectionIcons,
  icons,
} from "../../../../stack/environment/icons/iconExport";
import { getActiveDetections } from "../../../detections/components/utils";
import PercentageLabel from "../../../frameworks/components/PercentageLabel";
import ScenarioSetAggregatedFlow from "../../graph/ScenarioSetAggregatedFlow";

export default function ScenariosSetCard({
  row,
  setSelectedScenarioSet,
  index,
  fullScreen,
}) {
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);
  const { scenariosDemoMode, businessValueMode } = useContext(
    EnvFeatureFlagContext
  );

  const colors = useColors();
  const { user } = useAuth0();
  const { fetchAllSteps, getAllDetectedStepIDs } = useAPI();
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [scenarioSetDetectedSteps, setScenarioSetDetectedSteps] = useState([]);
  const [detectionInSteps, setDetectionInSteps] = useState([]);
  const { regularAndActiveSmartAsset: assets, isLoadingAll: assetsIsLoading } =
    useSmartAndRegularAssets();
  const { data: allSteps } = useQuery({
    queryKey: stepKeys.all,
    queryFn: fetchAllSteps,
    select: (data) =>
      data.data
        .filter((step) => !step.is_virtual)
        .map((step) => ({ ...step, id: step.step_id })),
  });

  const [stepsInScenario, setStepsInScenario] = useState([]);
  useEffect(() => {
    setStepsInScenario(
      allSteps
        ?.filter((step) => !step.is_virtual)
        ?.filter((step) => row?.reactFlow?.step_ids.includes(step.step_id))
    );
  }, [allSteps, row?.reactFlow?.step_ids]);

  const checkIfCardViewByUser = () => {
    const openBy = row?.reactFlow?.opened_by_user;
    if (openBy?.includes(user.email)) {
      return true;
    }
    return false;
  };

  const { data: detectedStepIDs } = useQuery({
    queryKey: stepKeys.detectedSteps(),
    enabled: detectionsActive !== false,
    queryFn: async () => {
      try {
        const response = await getAllDetectedStepIDs();
        return response.data;
      } catch (err) {
        return [];
      }
    },
  });

  useEffect(() => {
    if (detectedStepIDs && stepsInScenario) {
      const scenarioSetDetectedSteps = stepsInScenario.filter((step) => {
        return detectedStepIDs.includes(step.step_id);
      });

      setScenarioSetDetectedSteps(scenarioSetDetectedSteps);
      const allDetectionInSteps = [];
      scenarioSetDetectedSteps.forEach((step) => {
        allDetectionInSteps.push(...step.last_detections);
      });
      setDetectionInSteps(allDetectionInSteps);
    }
  }, [detectedStepIDs, stepsInScenario]);

  const activeDetections = getActiveDetections(detectionInSteps);
  const isDetected = detectionsActive && activeDetections.length > 0;

  const assetAtRisk =
    assets.find((asset) => asset.asset_id === row.reactFlow?.asset_id)?.name ||
    "";

  return (
    <Box
      position="relative"
      display={"flex"}
      flexDirection={!fullScreen ? "row-reverse" : "column"}
      sx={{
        borderRadius: "24px",
        border: isDetected
          ? "1px solid " + colors.error_48percent
          : checkIfCardViewByUser()
          ? "1px solid " + colors.grey_20percent
          : "1px solid " + colors.primary_32percent,
        boxShadow: "0px 0px 15.6px 0px " + colors.grey_20percent,
        backgroundColor: colors.common_white,
        padding: "12px",
        gap: "12px",
        height: fullScreen ? "auto" : "200px",
        transition: "height 0.3s ease-in-out",
        alignItems: !fullScreen && "flex-start",
        justifyContent: !fullScreen && "space-between",
        marginTop: isDetected ? "9px" : "0px",
      }}
    >
      {isDetected && (
        <Box
          position="absolute"
          top="-12px"
          left="40%"
          px={1}
          borderRadius="8px"
          bgcolor={colors.error_lighter}
          color={colors.error_500}
          boxShadow={"0px 0px 1px 0px " + colors.error_48percent}
          fontSize="14px"
          fontWeight="medium"
          display="flex"
          alignItems="center"
          padding="3px"
        >
          <SvgToIcon IconLogo={detectionIcons.DetectionRed} width={"16px"} />
          <Typography
            variant="caption"
            color={colors.error_500}
            padding="0px 4px"
          >
            Events Detected
          </Typography>
        </Box>
      )}
      {!fullScreen && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            height: "100%",
          }}
        >
          <CustomToolTip title={"Click to view detailed attacks paths"}>
            <Box
              sx={{
                cursor: "pointer",
                zIndex: 100,
                borderRadius: "8px",
                border: "1px solid " + colors.divider2,
                padding: "8px",
                display: "flex",
                alignItems: "center",
                backgroundColor: colors.common_white,
              }}
              onClick={() => {
                const params = new URLSearchParams(location.search);
                const scenarioSetId = encodeURIComponent(
                  row?.reactFlow.locations_and_phases_and_asset
                );
                params.set("selected_scenario_set", scenarioSetId);
                setSearchParams(params.toString());
                setSelectedScenarioSet(row?.reactFlow);
              }}
            >
              <SvgToIcon IconLogo={icons.ExpandScreenIcon} width={"16px"} />
            </Box>
          </CustomToolTip>
        </Box>
      )}
      <Box
        sx={{
          width: fullScreen ? "100%" : "72%",
          transition: "width 0.3s ease-in-out",
        }}
      >
        <ScenarioSetAggregatedFlow
          width={"100%"}
          scenarioSet={row?.reactFlow}
          setSelectedScenarioSet={setSelectedScenarioSet}
          key={`flow-${row?.id}-${fullScreen}`}
          fullScreen={fullScreen}
        />
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ flexDirection: fullScreen ? "row" : "column" }}
        p={1}
      >
        {/* Left Section */}
        <Box
          display="flex"
          alignItems="flex-start"
          gap={fullScreen ? 2 : 1}
          sx={{ flexDirection: fullScreen ? "row" : "column" }}
        >
          <Box>
            <LabelComponentProp
              label="At Risk"
              textTypography="caption"
              alignItems="flex-start"
              sx={{
                minHeight: "30px",
                gap: "6px",
              }}
            >
              <Typography variant="body2">
                {assetsIsLoading ? <LoadingIcon /> : assetAtRisk}
              </Typography>
            </LabelComponentProp>
          </Box>
          {!fullScreen && <Divider orientation={"horizontal"} flexItem />}
          <Box
            display="flex"
            flexDirection="row"
            gap={fullScreen ? 2 : 3}
            alignItems="flex-start"
          >
            {businessValueMode && (
              <>
                {fullScreen && <Divider orientation={"vertical"} flexItem />}
                <LabelComponentProp
                  label="Value at Risk"
                  textTypography="caption"
                  alignItems="flex-start"
                  sx={{
                    minHeight: "30px",
                    gap: "6px",
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={1}
                    alignItems="center"
                  >
                    <Typography variant="body2" color={colors.text_primary}>
                      ${formatNumberCompact(row.reactFlow?.value_at_risk)}
                    </Typography>
                  </Box>
                </LabelComponentProp>
              </>
            )}
            {(businessValueMode || fullScreen) && (
              <Divider orientation={"vertical"} flexItem />
            )}
            <LabelComponentProp
              label="Perimeter"
              textTypography="caption"
              alignItems="flex-start"
              sx={{
                minHeight: "30px",
                gap: "6px",
              }}
            >
              <Typography variant="body2" color={colors.text_primary}>
                {row.reactFlow?.locations_in_order[0]}
              </Typography>
            </LabelComponentProp>
            <Divider orientation={"vertical"} flexItem />
            {/* Mitigated Paths Block */}
            <LabelComponentProp
              label="Mitigated"
              textTypography="caption"
              alignItems="flex-start"
              sx={{
                minHeight: "30px",
                gap: "6px",
              }}
            >
              <Box
                display="flex"
                flexDirection="row"
                gap={"8px"}
                alignItems="center"
              >
                <PercentageLabel
                  percentage={row.coverage}
                  sx={{ borderRadius: "10px" }}
                />
                <CustomToolTip
                  title={
                    <Box display="flex" flexDirection="row" gap={0.5}>
                      <Typography variant="caption" color={colors.text_primary}>
                        {row.reactFlow?.mitigated_scenario_count} Paths
                        mitigated out of{" "}
                        {row.reactFlow?.mitigated_scenario_count +
                          row.reactFlow?.unmitigated_scenario_count}{" "}
                        paths for this attack scenario.
                      </Typography>
                    </Box>
                  }
                >
                  <InfoRounded
                    sx={{
                      width: "16px",
                      height: "16px",
                      color: colors.grey_48percent,
                    }}
                  />
                </CustomToolTip>
              </Box>
            </LabelComponentProp>
            {/* value at risk block */}
            {/* Divider and Detections Block */}
          </Box>
        </Box>
        {/* Right Section */}
        {fullScreen && (
          <Box>
            <CustomToolTip
              placement="top"
              title={"Click to view detailed attacks paths"}
              style={{
                border: "1px solid " + colors.divider2,
                borderRadius: "10px",
              }}
            >
              <Button
                endIcon={
                  <SvgToIcon IconLogo={icons.ExpandScreenIcon} width={"16px"} />
                }
                sx={{
                  borderRadius: "10px",
                  padding: 1,
                  border: "1px solid " + colors.divider2,
                  textTransform: "none",

                  "&:hover": {
                    background: colors.grey_200,
                  },
                }}
                onClick={() => {
                  const params = new URLSearchParams(location.search);
                  const scenarioSetId = encodeURIComponent(
                    row?.reactFlow.locations_and_phases_and_asset
                  );
                  params.set("selected_scenario_set", scenarioSetId);
                  setSearchParams(params.toString());
                  setSelectedScenarioSet(row?.reactFlow);
                }}
              >
                <Typography variant="overline"> Explore</Typography>
              </Button>
            </CustomToolTip>
          </Box>
        )}
      </Box>
    </Box>
  );
}
